/**
 * CurrentPlanCard — current plan + status summary (design Screen 4, top card).
 *
 * Reads the subscription state passed down from `BillingSettingsPage` (which
 * owns the single `useSubscription` call). Renders a plan label + status, and —
 * when the user is on a paid, active plan — a low-key "Cancel plan" text link
 * that opens the retention modal (`onCancel`). The destructive intent lives in
 * the modal, so this link is plain text in the error color, never a bright
 * button (design Screen 4 copy note).
 *
 * Status copy variants (design Screen 4):
 *   - active            → "{Plan} — Active"
 *   - trialing          → "{Plan} — Trial · ends {date}"
 *   - past_due          → "{Plan} — Past due" (amber styling)
 *   - canceled / null   → "Explorer (free)"
 *   - cancel_at_period_end → "Cancels on {date}" sub-line + "Manage in Stripe"
 *
 * @param {object} props
 * @param {object|null} props.subscription - GET /api/billing/subscription shape:
 *   { plan_code, status, current_period_end, cancel_at_period_end, trial_end }.
 * @param {boolean} [props.loading] - Whether the subscription is still loading.
 * @param {() => void} [props.onCancel] - Open the cancellation retention modal.
 * @param {() => void} [props.onManageInStripe] - Open the Customer Portal (used
 *   for the cancel-at-period-end "Manage in Stripe" affordance + reactivation).
 */

import React from 'react';
import styles from './CurrentPlanCard.module.css';

/** Human-readable plan names keyed by the backend `plan_code`. */
const PLAN_LABELS = {
  explorer: 'Explorer',
  fullservice: 'Full Service',
};

/** Paid plans that can be cancelled via the Customer Portal. */
const PAID_PLANS = new Set(['fullservice']);

/** Format an ISO date string as e.g. "Jun 20, 2026"; falls back to ''. */
function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/** Map a status to a status-chip tone class. */
function chipClass(styles_, status) {
  switch (status) {
    case 'active':
      return styles_.chipActive;
    case 'trialing':
      return styles_.chipTrialing;
    case 'past_due':
      return styles_.chipPastDue;
    case 'canceled':
      return styles_.chipCanceled;
    default:
      return styles_.chipNeutral;
  }
}

export default function CurrentPlanCard({
  subscription,
  loading,
  onCancel,
  onManageInStripe,
}) {
  if (loading) {
    return (
      <div className={styles.card} data-testid="current-plan-card" aria-busy="true">
        <div className={styles.skeletonLine} />
        <div className={styles.skeletonLineShort} />
      </div>
    );
  }

  const planCode = subscription?.plan_code || 'explorer';
  const status = subscription?.status || null;
  const planLabel = PLAN_LABELS[planCode] || planCode;
  const isPaid = PAID_PLANS.has(planCode);
  const isFree = planCode === 'explorer' || !status;
  const cancelAtPeriodEnd = !!subscription?.cancel_at_period_end;
  const periodEnd = formatDate(subscription?.current_period_end);
  const trialEnd = formatDate(subscription?.trial_end);

  // Status chip label.
  let chipLabel;
  if (isFree) {
    chipLabel = 'Free';
  } else if (status === 'active') {
    chipLabel = 'Active';
  } else if (status === 'trialing') {
    chipLabel = 'Trial';
  } else if (status === 'past_due') {
    chipLabel = 'Past due';
  } else if (status === 'canceled') {
    chipLabel = 'Canceled';
  } else {
    chipLabel = status;
  }

  // Cancellation is delegated to the Stripe Customer Portal — only show the
  // in-app "Cancel plan" link on a paid plan that is active/trialing and not
  // already scheduled to cancel.
  const showCancel =
    isPaid &&
    !cancelAtPeriodEnd &&
    (status === 'active' || status === 'trialing');

  return (
    <div className={styles.card} data-testid="current-plan-card">
      <div className={styles.row}>
        <div className={styles.planText}>
          <span className={styles.planName}>{planLabel}</span>
          <span
            className={`${styles.chip} ${chipClass(styles, isFree ? null : status)}`}
            data-testid="current-plan-status"
          >
            {chipLabel}
          </span>
        </div>
      </div>

      {/* Trial sub-line — concrete date beats "in 7 days" (design Screen 4). */}
      {status === 'trialing' && trialEnd && (
        <p className={styles.subline}>Trial ends {trialEnd}.</p>
      )}

      {/* Past-due sub-line — amber, recoverable framing. */}
      {status === 'past_due' && (
        <p className={`${styles.subline} ${styles.sublineWarning}`}>
          We couldn't process your last payment. Update your payment method below
          to keep {planLabel} active.
        </p>
      )}

      {/* Scheduled cancellation — keep access until period end. */}
      {cancelAtPeriodEnd && periodEnd && (
        <p className={styles.subline}>
          Cancels on {periodEnd} — you'll keep access until then.{' '}
          {onManageInStripe && (
            <button
              type="button"
              className={styles.manageLink}
              onClick={onManageInStripe}
              data-testid="manage-in-stripe-btn"
            >
              Manage in Stripe
            </button>
          )}
        </p>
      )}

      {/* Free plan — point at what the paid plans add. */}
      {isFree && (
        <p className={styles.subline}>
          You're on the free Explorer plan. Offers on cutthe6.com homes are free;
          see Plans &amp; Pricing to list your home or prepare paperwork for a
          home listed elsewhere.
        </p>
      )}

      {showCancel && (
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.cancelLink}
            onClick={onCancel}
            data-testid="cancel-plan-btn"
          >
            Cancel plan
          </button>
        </div>
      )}
    </div>
  );
}
